// What the portal knows about who is using it, and how a screen asks.
//
// Split from the provider component so this module exports no component: the
// repo lints with `react-refresh/only-export-components` at --max-warnings 0,
// and a file mixing a component with a hook trips it.

import { createContext, useContext } from 'react';

import type { ActingScope, PortalIdentity } from './api';

/**
 * Three states, and the first one matters.
 *
 * `checking` is the boot refresh in flight. It is a state rather than an
 * assumption of signed-out because assuming signed-out would flash the sign-in
 * screen at somebody who is signed in, every time they reload.
 */
export type PortalSession =
  | { status: 'checking' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; identity: PortalIdentity };

export interface PortalSessionValue {
  session: PortalSession;
  /**
   * Sign in. Resolves when the session is established; rejects with the
   * service's own refusal wording, which the screen shows as-is.
   */
  signIn(email: string, password: string, keepSignedIn: boolean): Promise<void>;
  /**
   * Take up a session somebody else's door already granted.
   *
   * The claim journey ends in a token: taking hold of a subscription proves who
   * you are as surely as a sign-in does, and the backend answers it with the
   * same grant and the same refresh cookie. So the journey hands the token here
   * rather than asking the person to sign in again to the account they just
   * made.
   *
   * Not a second way to authenticate — there is no credential in this call. It
   * is how a token that has already been earned reaches the one place this app
   * keeps one.
   */
  adoptSession(accessToken: string): Promise<void>;
  /**
   * Move to another scope this person is entitled to, and re-boot on it.
   *
   * The switcher's one call. It ends in the same place a sign-in ends — the
   * identity re-read from GET /portal/api/me/ — so what this tab believes
   * about who it is acting as after a switch came from the backend and not
   * from what the switch was asked for. That matters here more than at the
   * sign-in door: the whole point of a switch is that the surface changes, and
   * a tab that redrew from its own request rather than from the answer would
   * be a tab that could draw a customer's nav on a scope it did not get.
   *
   * Rejects with the service's own refusal wording where the switch is not
   * allowed, leaving the current session exactly as it was.
   */
  switchScope(scope: ActingScope): Promise<void>;
  /** Revoke the refresh family and drop the token this tab is holding. */
  signOut(): Promise<void>;
  /**
   * Fetch from the portal API bearing the current access token, renewing it
   * once if the service says it has expired. The template every later phase's
   * data call copies — no screen should be reading the token itself.
   */
  authorizedFetch(path: string, init?: RequestInit): Promise<Response>;
}

export const PortalSessionContext = createContext<PortalSessionValue | null>(null);

/** The session, from inside the provider. Throws outside it rather than guessing. */
export function usePortalSession(): PortalSessionValue {
  const value = useContext(PortalSessionContext);
  if (!value) {
    throw new Error('usePortalSession used outside PortalSessionProvider');
  }
  return value;
}
